// btc15/usePulse.js — poll the one-second lane without ever stacking calls.
//
// setTimeout chained off the previous call finishing, not setInterval: if the
// network stalls, calls do not queue up and then land in a burst. It pauses
// while the tab is hidden (a PWA in the background has nothing to draw, and
// Kalshi should not be asked on its behalf), catches up the moment it is
// visible again, and backs off when the server is failing.
//
// `fetcher` is passed in so the phone tab and the desktop tab each use their
// own API base and auth, and so tests can hand it a fake.
import { useEffect, useRef, useState } from "react";
import { emptyPulse } from "./pulse";

export default function usePulse(fetcher, { enabled = true, intervalMs = 1000 } = {}) {
  const [state, setState] = useState(emptyPulse);
  const fetchRef = useRef(fetcher);
  fetchRef.current = fetcher;

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true, timer = null, failures = 0;

    const schedule = (ms) => { if (alive) { clearTimeout(timer); timer = setTimeout(run, ms); } };
    const run = async () => {
      if (!alive) return;
      if (typeof document !== "undefined" && document.hidden) { schedule(1000); return; }
      const t0 = Date.now();
      try {
        const data = await fetchRef.current();
        if (!alive) return;
        failures = 0;
        const done = Date.now();
        setState({ data, receivedAt: done, ok: true, failures: 0, rttMs: done - t0 });
        schedule(Math.max(150, intervalMs - (done - t0)));
      } catch {
        if (!alive) return;
        failures += 1;
        setState((s) => ({ ...s, ok: false, failures }));
        schedule(Math.min(8000, intervalMs * 2 ** Math.min(failures, 3)));
      }
    };
    const onVisible = () => { if (typeof document !== "undefined" && !document.hidden) schedule(0); };
    document.addEventListener("visibilitychange", onVisible);
    run();
    return () => { alive = false; clearTimeout(timer); document.removeEventListener("visibilitychange", onVisible); };
  }, [enabled, intervalMs]);

  return state;
}
